import type { WebhookEndpoint } from '@compass/client/types/webhook';
import { MoreHorizontal } from 'lucide-react';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { WebhookPermissions } from '@/hooks/useShouldShowWebhooksPage';

import {
  type EndpointStatusAction,
  useEndpointStatusAction,
  useRotateWebhookSecret,
  useSendWebhookTestEvent,
} from '../hooks/useWebhookEndpoints';
import {
  endpointStatusActions,
  isActionAllowedOnEndpoint,
  isEndpointActive,
  isEndpointManaged,
  MANAGED_ENDPOINT_HINT,
  MANAGED_ENDPOINT_LABEL,
  webhookErrorMessage,
  webhooksPath,
} from '../utils';
import { ConfirmEndpointActionDialog } from './ConfirmEndpointActionDialog';
import { EndpointFormDialog } from './EndpointFormDialog';
import { RotateSecretDialog } from './RotateSecretDialog';
import { ShowSigningSecretDialog } from './ShowSigningSecretDialog';

export type EndpointAction = 'test' | 'deliveries' | 'edit' | 'rotate' | EndpointStatusAction;

interface SecretState {
  endpoint: WebhookEndpoint;
  secret: string;
  rotated: boolean;
}

interface UseEndpointActionsOptions {
  organizationId: string;
  permissions: WebhookPermissions;
  /** Called after a successful delete (the detail page navigates away). */
  onDeleted?: () => void;
}

/**
 * Owns every endpoint action and its dialogs, so the endpoints table and the
 * endpoint detail page behave identically. Render `dialogs` once; call `run`
 * from a menu item or button; `openCreate` starts the Add endpoint flow.
 */
export function useEndpointActions({
  organizationId,
  permissions,
  onDeleted,
}: UseEndpointActionsOptions) {
  const navigate = useNavigate();

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<WebhookEndpoint | null>(null);
  const [confirming, setConfirming] = useState<{
    action: EndpointStatusAction;
    endpoint: WebhookEndpoint;
  } | null>(null);
  const [rotating, setRotating] = useState<WebhookEndpoint | null>(null);
  const [secret, setSecret] = useState<SecretState | null>(null);

  const statusMutation = useEndpointStatusAction(organizationId);
  const rotateMutation = useRotateWebhookSecret(organizationId);
  const testMutation = useSendWebhookTestEvent(organizationId);

  /**
   * `fromSecretDialog`: sent from the one-time secret dialog. The toast then has
   * no "View delivery" link — navigating away (from the detail page) would
   * unmount that dialog and lose a secret the viewer may not have copied yet.
   */
  const sendTest = (endpoint: WebhookEndpoint, fromSecretDialog = false) => {
    if (!endpoint.id) return;
    const endpointId = endpoint.id;
    testMutation.mutate(endpointId, {
      onSuccess: data => {
        const deliveryId = data?.delivery_id;
        toast.success('Test event sent', {
          description: fromSecretDialog
            ? 'It is delivered within a few seconds. See it under Deliveries.'
            : 'It is delivered within a few seconds.',
          action:
            permissions.canListDeliveries && deliveryId && !fromSecretDialog
              ? {
                  label: 'View delivery',
                  onClick: () =>
                    navigate(
                      webhooksPath(organizationId, { tab: 'deliveries', endpointId, deliveryId }),
                    ),
                }
              : undefined,
        });
      },
      onError: err => toast.error(webhookErrorMessage(err, 'Failed to send the test event')),
    });
  };

  const run = (action: EndpointAction, endpoint: WebhookEndpoint) => {
    // The menus never offer these on a managed endpoint; the server would
    // refuse them anyway (FailedPrecondition).
    if (!isActionAllowedOnEndpoint(action, endpoint)) {
      toast.info(MANAGED_ENDPOINT_HINT);
      return;
    }
    switch (action) {
      case 'test':
        sendTest(endpoint);
        return;
      case 'deliveries':
        if (endpoint.id) {
          navigate(webhooksPath(organizationId, { tab: 'deliveries', endpointId: endpoint.id }));
        }
        return;
      case 'edit':
        setEditing(endpoint);
        return;
      case 'rotate':
        setRotating(endpoint);
        return;
      default:
        setConfirming({ action, endpoint });
    }
  };

  const confirmStatus = () => {
    if (!confirming?.endpoint.id) return;
    const { action, endpoint } = confirming;
    statusMutation.mutate(
      { action, endpointId: endpoint.id as string },
      {
        onSuccess: result => {
          setConfirming(null);
          const done: Record<EndpointStatusAction, string> = {
            pause: 'Endpoint paused',
            resume: 'Endpoint resumed',
            disable: 'Endpoint disabled',
            delete: 'Endpoint deleted',
          };
          let description: string | undefined;
          if (result.replayOperationId) {
            description = 'Events missed while paused are being replayed.';
          } else if (result.cancelledDeliveries) {
            description = `${result.cancelledDeliveries} pending ${
              result.cancelledDeliveries === 1 ? 'delivery was' : 'deliveries were'
            } cancelled.`;
          }
          toast.success(done[action], { description });
          if (result.dispatcherError) {
            toast.warning(
              action === 'resume'
                ? 'The endpoint is active, but events missed while paused could not be replayed. Contact 100ms support.'
                : 'The change is saved, but pending deliveries could not be cancelled yet.',
            );
          }
          if (action === 'delete') onDeleted?.();
        },
        onError: err => toast.error(webhookErrorMessage(err, 'The change could not be saved')),
      },
    );
  };

  const confirmRotate = () => {
    if (!rotating?.id) return;
    const endpoint = rotating;
    rotateMutation.mutate(endpoint.id as string, {
      onSuccess: data => {
        setRotating(null);
        // The plaintext now lives only in `secret` state; drop it from the
        // mutation's cached result.
        rotateMutation.reset();
        if (data?.signing_secret) {
          setSecret({
            endpoint: data.endpoint ?? endpoint,
            secret: data.signing_secret,
            rotated: true,
          });
        } else {
          toast.warning('The secret was rotated, but the new one could not be shown.', {
            description: 'Rotate again to issue a secret you can copy.',
          });
        }
      },
      onError: err => toast.error(webhookErrorMessage(err, 'Failed to rotate the signing secret')),
    });
  };

  const canSendTestFromSecret =
    !!secret &&
    permissions.canSendTestEvent &&
    isEndpointActive(secret.endpoint) &&
    !isEndpointManaged(secret.endpoint);

  const dialogs = (
    <>
      {(creating || editing) && (
        <EndpointFormDialog
          organizationId={organizationId}
          endpoint={editing ?? undefined}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onCreated={(endpoint, signingSecret) =>
            setSecret({ endpoint, secret: signingSecret, rotated: false })
          }
        />
      )}

      {confirming && (
        <ConfirmEndpointActionDialog
          action={confirming.action}
          endpoint={confirming.endpoint}
          isPending={statusMutation.isPending}
          onConfirm={confirmStatus}
          onClose={() => setConfirming(null)}
        />
      )}

      {rotating && (
        <RotateSecretDialog
          endpoint={rotating}
          isPending={rotateMutation.isPending}
          onConfirm={confirmRotate}
          onClose={() => setRotating(null)}
        />
      )}

      {secret && (
        <ShowSigningSecretDialog
          secret={secret.secret}
          title={secret.rotated ? 'New signing secret' : 'Endpoint created'}
          description={
            secret.rotated
              ? 'Copy the new secret now. The previous one keeps working until it expires.'
              : 'Copy the signing secret now. It will not be shown again.'
          }
          onClose={() => setSecret(null)}
          onSendTestEvent={
            canSendTestFromSecret ? () => sendTest(secret.endpoint, true) : undefined
          }
          isSendingTestEvent={testMutation.isPending}
        />
      )}
    </>
  );

  return {
    run,
    dialogs,
    openCreate: () => setCreating(true),
    isSendingTest: testMutation.isPending,
  };
}

export type EndpointActionsController = ReturnType<typeof useEndpointActions>;

interface EndpointActionsMenuProps {
  endpoint: WebhookEndpoint;
  permissions: WebhookPermissions;
  onAction: (action: EndpointAction, endpoint: WebhookEndpoint) => void;
  /** The detail page already is the endpoint's page, so it hides "View deliveries". */
  showViewDeliveries?: boolean;
}

/**
 * The row `⋯` menu: test, deliveries, pause/resume, rotate, edit, then
 * disable/delete. A managed endpoint (set up by 100ms support) offers only
 * View deliveries, under a "Managed by 100ms" label.
 */
export function EndpointActionsMenu({
  endpoint,
  permissions,
  onAction,
  showViewDeliveries = true,
}: EndpointActionsMenuProps) {
  const { canPause, canResume, canDisable } = endpointStatusActions(endpoint.status);
  const active = isEndpointActive(endpoint);
  const managed = isEndpointManaged(endpoint);
  const allowed = (action: EndpointAction) => isActionAllowedOnEndpoint(action, endpoint);
  const item = (
    action: EndpointAction,
    label: string,
    extra?: { disabled?: boolean; destructive?: boolean },
  ) => (
    <DropdownMenuItem
      key={action}
      disabled={extra?.disabled}
      className={extra?.destructive ? 'text-destructive focus:text-destructive' : undefined}
      onSelect={() => onAction(action, endpoint)}
      data-analytics-id={`webhooks:endpoint:menu-${action}`}
    >
      {label}
    </DropdownMenuItem>
  );

  const primary = [
    permissions.canSendTestEvent &&
      allowed('test') &&
      item('test', 'Send test event', { disabled: !active }),
    showViewDeliveries && permissions.canListDeliveries && item('deliveries', 'View deliveries'),
    canPause && permissions.canPauseEndpoint && allowed('pause') && item('pause', 'Pause'),
    canResume && permissions.canResumeEndpoint && allowed('resume') && item('resume', 'Resume'),
    permissions.canRotateSecret && allowed('rotate') && item('rotate', 'Rotate secret'),
    permissions.canUpdateEndpoint && allowed('edit') && item('edit', 'Edit'),
  ].filter(Boolean);
  const danger = [
    canDisable &&
      permissions.canDisableEndpoint &&
      allowed('disable') &&
      item('disable', 'Disable', { destructive: true }),
    permissions.canDeleteEndpoint &&
      allowed('delete') &&
      item('delete', 'Delete', { destructive: true }),
  ].filter(Boolean);

  if (primary.length === 0 && danger.length === 0) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <MoreHorizontal className="h-4 w-4" />
          <span className="sr-only">Endpoint actions</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {managed && (
          <>
            <DropdownMenuLabel className="max-w-[240px] font-normal">
              <span className="block text-xs font-medium">{MANAGED_ENDPOINT_LABEL}</span>
              <span className="block text-xs text-muted-foreground">
                Contact 100ms support to change this endpoint.
              </span>
            </DropdownMenuLabel>
            {primary.length > 0 && <DropdownMenuSeparator />}
          </>
        )}
        {primary}
        {primary.length > 0 && danger.length > 0 && <DropdownMenuSeparator />}
        {danger}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
